/**
 * Evento de outro produtor em que a pessoa foi convidada a colaborar. A API
 * marca esses eventos na lista do painel com `collaboration`:
 * `{ permissions: string[], owner: { id, name } }`. Evento próprio não tem
 * esse campo e a pessoa pode tudo nele.
 */
export const EVENT_PERMISSIONS = {
    EDIT: 'event.edit',
    SALES_VIEW: 'event.sales.view',
    PARTICIPANTS_VIEW: 'participants.view',
    CHECKIN_TICKETS: 'ticket',
    CHECKIN_PRODUCTS: 'product',
};

// Na ordem em que o aviso lista o que foi liberado.
const PERMISSION_LABELS = {
    [EVENT_PERMISSIONS.EDIT]: 'editar o evento',
    [EVENT_PERMISSIONS.SALES_VIEW]: 'ver vendas, pedidos e valores',
    [EVENT_PERMISSIONS.PARTICIPANTS_VIEW]: 'ver os participantes',
    [EVENT_PERMISSIONS.CHECKIN_TICKETS]: 'validar ingressos',
    [EVENT_PERMISSIONS.CHECKIN_PRODUCTS]: 'validar produtos',
};

export function isCollaboration(event) {
    return !!event?.collaboration;
}

/** O que a pessoa pode neste evento: o dono pode tudo, o colaborador só o que foi liberado. */
export function canInEvent(event, ...anyOf) {
    if (!isCollaboration(event)) return true;
    if (anyOf.length === 0) return true;

    const granted = event.collaboration.permissions || [];

    return anyOf.some((permission) => granted.includes(permission));
}

/** O que foi liberado, em texto, na ordem de `PERMISSION_LABELS`. */
export function grantedLabels(event) {
    const granted = event?.collaboration?.permissions || [];

    return Object.keys(PERMISSION_LABELS)
        .filter((permission) => granted.includes(permission))
        .map((permission) => PERMISSION_LABELS[permission]);
}

/** "a, b e c" */
export function joinLabels(labels) {
    if (labels.length <= 1) return labels.join('');

    return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}

/**
 * O que cada aba do evento exige de quem só colabora. Lista vazia: a aba abre
 * para qualquer colaborador. `null`: só o dono do evento. Quem não tem uma das
 * permissões vê a página bloqueada, não escondida.
 */
export const COLLABORATION_TAB_REQUIREMENTS = {
    detail: [],
    tickets: [],
    products: [],
    orders: [EVENT_PERMISSIONS.SALES_VIEW],
    participant: [EVENT_PERMISSIONS.PARTICIPANTS_VIEW],
    // Campos adicionais fazem parte da edição do evento.
    additional: [EVENT_PERMISSIONS.EDIT],
    // Gestão do evento (quem tem acesso, cupons e despesas): a API só aceita o
    // dono, então para o colaborador a página fica bloqueada.
    permissions: null,
    promotionalCode: null,
    expenses: null,
};

/**
 * `null` quando a aba está liberada; senão `{ needs }`, com o que o convite
 * teria de liberar em texto (`null` quando a aba é só do dono).
 */
export function tabRestriction(event, tabName) {
    if (!isCollaboration(event)) return null;

    const required = COLLABORATION_TAB_REQUIREMENTS[tabName];

    if (required === undefined || (Array.isArray(required) && required.length === 0)) return null;
    if (required === null) return { needs: null };
    if (canInEvent(event, ...required)) return null;

    return { needs: joinLabels(required.map((permission) => PERMISSION_LABELS[permission])) };
}
